// ============================================
// UTILITY FUNCTIONS
// ============================================

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, formatDistanceToNow } from 'date-fns';
import { tr, enUS } from 'date-fns/locale';
import i18next from 'i18next';

/**
 * Merge Tailwind CSS classes
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Aktif dile göre date-fns locale döner
 */
function getDateLocale() {
  const lang = i18next.language || 'tr';
  return lang.startsWith('en') ? enUS : tr;
}

/**
 * i18n destekli label objesi oluşturur (backward compatible)
 * Object.entries() ve doğrudan erişim destekler
 */
function createI18nLabels(namespace: string, keys: string[]): Record<string, string> {
  const obj: Record<string, string> = {};
  keys.forEach(key => {
    Object.defineProperty(obj, key, {
      get: () => i18next.t(`${namespace}.${key}`, { defaultValue: key }),
      enumerable: true,
      configurable: true,
    });
  });
  return obj;
}

/**
 * Get user-friendly error message from API error
 */
export function getErrorMessage(error: any, defaultMessage?: string): string {
  const t = (key: string) => i18next.t(key);
  const status = error?.response?.status || error?.status;
  const serverMessage = error?.response?.data?.message || error?.message;

  // Status code bazlı mesajlar
  const statusMessages: Record<number, string> = {
    400: t('common:errors.http.400'),
    401: t('common:errors.http.401'),
    403: t('common:errors.http.403'),
    404: t('common:errors.http.404'),
    409: t('common:errors.http.409'),
    413: t('common:errors.http.413'),
    415: t('common:errors.http.415'),
    422: t('common:errors.http.422'),
    429: t('common:errors.http.429'),
    500: t('common:errors.http.500'),
    502: t('common:errors.http.502'),
    503: t('common:errors.http.503'),
    504: t('common:errors.http.504'),
  };

  // Bilinen sunucu mesajları → i18n key mapping
  const knownMessages: Record<string, string> = {
    // Auth
    'Invalid credentials': t('common:errors.server.invalidCredentials'),
    'Invalid email or password': t('common:errors.server.invalidCredentials'),
    'Email already exists': t('common:errors.server.emailExists'),
    'Email already registered': t('common:errors.server.emailExists'),
    'User not found': t('common:errors.server.userNotFound'),
    'Account disabled': t('common:errors.server.accountDisabled'),
    'Account locked': t('common:errors.server.accountLocked'),
    'Invalid token': t('common:errors.server.invalidToken'),
    'Token expired': t('common:errors.server.tokenExpired'),
    'Password reset required': t('common:errors.server.passwordResetRequired'),
    'Unauthorized': t('common:errors.server.unauthorized'),
    'Forbidden': t('common:errors.server.forbidden'),
    'Access denied': t('common:errors.server.accessDenied'),

    // Validation
    'Validation failed': t('common:errors.server.validationFailed'),
    'Invalid data': t('common:errors.server.invalidData'),
    'Required field missing': t('common:errors.server.requiredFieldMissing'),
    'Invalid date format': t('common:errors.server.invalidDateFormat'),
    'Invalid email format': t('common:errors.server.invalidEmailFormat'),
    'Invalid phone format': t('common:errors.server.invalidPhoneFormat'),
    'Password too weak': t('common:errors.server.passwordTooWeak'),
    'Passwords do not match': t('common:errors.server.passwordsMismatch'),

    // File upload
    'No file provided': t('common:errors.server.noFileProvided'),
    'File too large': t('common:errors.server.fileTooLarge'),
    'Invalid file type': t('common:errors.server.invalidFileType'),
    'File upload failed': t('common:errors.server.fileUploadFailed'),
    'Unsupported file format': t('common:errors.server.unsupportedFileFormat'),
    'Max file size exceeded': t('common:errors.server.maxFileSizeExceeded'),

    // Review
    'Already reviewed this company': t('common:errors.server.alreadyReviewed'),
    'Already reviewed': t('common:errors.server.alreadyReviewed'),
    'Review not found': t('common:errors.server.reviewNotFound'),

    // Application
    'Already applied': t('common:errors.server.alreadyApplied'),
    'Already applied to this job': t('common:errors.server.alreadyApplied'),
    'Application not found': t('common:errors.server.applicationNotFound'),
    'Job not found': t('common:errors.server.jobNotFound'),
    'Job expired': t('common:errors.server.jobExpired'),
    'Job closed': t('common:errors.server.jobClosed'),
    'Application deadline passed': t('common:errors.server.applicationDeadlinePassed'),

    // Certificate
    'Certificate not found': t('common:errors.server.certificateNotFound'),
    'Certificate already exists': t('common:errors.server.certificateExists'),
    'Invalid certificate number': t('common:errors.server.invalidCertificateNumber'),
    'Certificate expired': t('common:errors.server.certificateExpired'),

    // Sea service
    'Sea service record not found': t('common:errors.server.seaServiceNotFound'),
    'Invalid date range': t('common:errors.server.invalidDateRange'),
    'End date before start date': t('common:errors.server.endDateBeforeStart'),

    // Document
    'Document not found': t('common:errors.server.documentNotFound'),
    'Folder not found': t('common:errors.server.folderNotFound'),
    'Document already exists': t('common:errors.server.documentExists'),

    // Company
    'Company not found': t('common:errors.server.companyNotFound'),

    // Profile
    'Profile not found': t('common:errors.server.profileNotFound'),
    'Profile update failed': t('common:errors.server.profileUpdateFailed'),

    // Reference
    'Reference not found': t('common:errors.server.referenceNotFound'),
    'Reference already exists': t('common:errors.server.referenceExists'),

    // Favorite
    'Already in favorites': t('common:errors.server.alreadyInFavorites'),
    'Not in favorites': t('common:errors.server.notInFavorites'),

    // Notification
    'Notification not found': t('common:errors.server.notificationNotFound'),

    // Generic
    'Not found': t('common:errors.server.notFound'),
    'Internal server error': t('common:errors.server.internalServerError'),
    'Service unavailable': t('common:errors.server.serviceUnavailable'),
    'Network error': t('common:errors.server.networkError'),
    'Request timeout': t('common:errors.server.requestTimeout'),
    'Too many requests': t('common:errors.server.tooManyRequests'),
  };

  // Önce sunucu mesajını kontrol et
  if (serverMessage) {
    // Birebir eşleşme
    if (knownMessages[serverMessage]) {
      return knownMessages[serverMessage];
    }

    // Kısmi eşleşme (case insensitive)
    const lowerMessage = serverMessage.toLowerCase();
    for (const [key, value] of Object.entries(knownMessages)) {
      if (lowerMessage.includes(key.toLowerCase())) {
        return value;
      }
    }
  }

  // Status code mesajı
  if (status && statusMessages[status]) {
    return statusMessages[status];
  }

  // Sunucu mesajı varsa ve makul uzunluktaysa göster
  if (serverMessage && typeof serverMessage === 'string' && serverMessage.length < 200) {
    return serverMessage;
  }

  return defaultMessage || t('common:errors.default');
}

/**
 * Format number with locale
 */
export function formatNumber(value: number): string {
  const lang = i18next.language || 'tr';
  return new Intl.NumberFormat(lang === 'en' ? 'en-US' : 'tr-TR').format(value);
}

/**
 * Format currency
 */
export function formatCurrency(value: number, currency: string = 'USD'): string {
  const lang = i18next.language || 'tr';
  return new Intl.NumberFormat(lang === 'en' ? 'en-US' : 'tr-TR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format date
 */
export function formatDate(date: string | Date, formatStr: string = 'dd MMM yyyy'): string {
  return format(new Date(date), formatStr, { locale: getDateLocale() });
}

/**
 * Format relative time (null-safe)
 */
export function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return i18next.t('common:new');
  try {
    const parsedDate = new Date(date);
    if (isNaN(parsedDate.getTime())) return i18next.t('common:new');
    return formatDistanceToNow(parsedDate, { addSuffix: true, locale: getDateLocale() });
  } catch {
    return i18next.t('common:new');
  }
}

/**
 * Truncate text
 */
export function truncate(text: string, length: number): string {
  if (text.length <= length) return text;
  return text.slice(0, length) + '...';
}

/**
 * Get initials from name
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Sleep utility
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Status badge variant mapper
 */
export function getStatusVariant(status: string): 'success' | 'warning' | 'error' | 'info' | 'default' {
  const statusMap: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
    ACTIVE: 'success',
    APPROVED: 'success',
    ACCEPTED: 'success',
    HIRED: 'success',
    VERIFIED: 'success',
    PENDING: 'warning',
    PENDING_VERIFICATION: 'warning',
    IN_REVIEW: 'warning',
    SHORTLISTED: 'info',
    INTERVIEW: 'info',
    OFFERED: 'info',
    PAUSED: 'warning',
    DRAFT: 'default',
    INACTIVE: 'default',
    REJECTED: 'error',
    WITHDRAWN: 'error',
    SUSPENDED: 'error',
    CLOSED: 'default',
    EXPIRED: 'default',
  };
  return statusMap[status] || 'default';
}

/**
 * Application status labels (i18n destekli, backward compatible)
 * Kullanım: applicationStatusLabels['PENDING'] veya Object.entries(applicationStatusLabels)
 */
export const applicationStatusLabels: Record<string, string> = createI18nLabels('enums:applicationStatus', [
  'PENDING', 'REVIEWED', 'SHORTLISTED', 'INTERVIEW', 'OFFERED', 'ACCEPTED', 'REJECTED', 'WITHDRAWN',
]);

/**
 * Job status labels (i18n destekli, backward compatible)
 */
export const jobStatusLabels: Record<string, string> = createI18nLabels('enums:jobStatus', [
  'DRAFT', 'ACTIVE', 'PAUSED', 'CLOSED', 'EXPIRED',
]);

/**
 * Department labels (i18n destekli, backward compatible)
 */
export const departmentLabels: Record<string, string> = createI18nLabels('enums:department', [
  'DECK', 'ENGINE', 'ELECTRICAL', 'CATERING', 'SAFETY', 'MEDICAL', 'RADIO', 'CREWING', 'FINANCE', 'SALES', 'IT', 'LOGISTICS', 'MANAGEMENT', 'OFFSHORE', 'OTHER',
]);

/**
 * Vessel type labels (i18n destekli, backward compatible)
 */
export const vesselTypeLabels: Record<string, string> = createI18nLabels('enums:vesselType', [
  'BULK_CARRIER', 'TANKER', 'CONTAINER', 'GENERAL_CARGO', 'RORO',
  'PASSENGER', 'OFFSHORE', 'TUG', 'YACHT', 'FISHING', 'OTHER',
]);

/**
 * Certificate category labels (i18n destekli, backward compatible)
 */
export const certificateCategoryLabels: Record<string, string> = createI18nLabels('enums:certificateCategory', [
  'ACİL DURUM VE GÜVENLİK', 'TANKER VE YÜK OPERASYONLARI', 'YOLCU VE ÖZEL GEMİLER',
  'GÜVERTE OPERASYONEL', 'MAKİNE OPERASYONEL', 'OFFSHORE VE ÖZEL ENDÜSTRİ', 'YEMEK VE HİJYEN',
]);

/**
 * Calculate profile completion percentage
 * Synchronized with backend calculation
 */
export function calculateProfileCompletion(profile: Record<string, unknown>): number {
  if (!profile) return 0;

  // If backend already provides profileCompleteness, use it
  if (typeof profile.profileCompleteness === 'number') {
    return profile.profileCompleteness;
  }

  let score = 0;

  // Basic info (20%)
  if (profile.firstName && profile.lastName) score += 5;
  if (profile.nationality) score += 5;
  if (profile.dateOfBirth) score += 5;
  if (profile.gender) score += 5;

  // Professional info (20%)
  if (profile.currentRankId || (profile.rank as any)?.id) score += 10;
  if (profile.department) score += 5;
  const expYears = profile.experienceYears as number;
  if (expYears !== undefined && expYears > 0) score += 5;

  // Contact (10%)
  if (profile.phone) score += 5;
  if (profile.city && profile.country) score += 5;

  // Bio (10%)
  const bio = profile.bio as string;
  if (bio && bio.length >= 50) score += 10;

  // Avatar (10%)
  if (profile.avatarUrl) score += 10;

  // Certificates (15%)
  const certs = profile.certificates as unknown[];
  const certCount = certs?.length || 0;
  if (certCount >= 3) score += 15;
  else if (certCount >= 1) score += certCount * 5;

  // Sea Service (15%)
  const services = profile.seaServiceRecords as unknown[];
  const serviceCount = services?.length || 0;
  if (serviceCount >= 2) score += 15;
  else if (serviceCount >= 1) score += 10;

  return Math.min(100, score);
}

/**
 * Get file extension
 */
export function getFileExtension(filename: string): string {
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);
}

/**
 * Format file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Days until expiry
 */
export function daysUntilExpiry(date: string | Date): number {
  const now = new Date();
  const expiry = new Date(date);
  const diff = expiry.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/**
 * Denizcilik sektöründe kullanılan ülke kodları
 */
export const COUNTRY_CODES = [
  // Avrupa
  'AD', 'AL', 'AT', 'BA', 'BE', 'BG', 'BY', 'CH', 'CY', 'CZ',
  'DE', 'DK', 'EE', 'ES', 'FI', 'FO', 'FR', 'GB', 'GE', 'GI',
  'GR', 'HR', 'HU', 'IE', 'IS', 'IT', 'LI', 'LT', 'LU', 'LV',
  'MC', 'MD', 'ME', 'MK', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO',
  'RS', 'RU', 'SE', 'SI', 'SK', 'SM', 'TR', 'UA', 'XK',
  // Asya & Orta Doğu
  'AE', 'AF', 'AM', 'AZ', 'BD', 'BH', 'BN', 'BT', 'CN', 'HK',
  'ID', 'IL', 'IN', 'IQ', 'IR', 'JP', 'JO', 'KH', 'KR', 'KW',
  'KZ', 'LA', 'LB', 'LK', 'MM', 'MN', 'MO', 'MV', 'MY', 'NP',
  'OM', 'PH', 'PK', 'QA', 'SA', 'SG', 'SY', 'TH', 'TJ', 'TL',
  'TM', 'TW', 'UZ', 'VN', 'YE',
  // Amerika
  'AG', 'AR', 'BB', 'BM', 'BO', 'BR', 'BS', 'BZ', 'CA', 'CL',
  'CO', 'CR', 'CU', 'CW', 'DM', 'DO', 'EC', 'GD', 'GT', 'GY',
  'HN', 'HT', 'JM', 'KN', 'KY', 'LC', 'MX', 'NI', 'PA', 'PE',
  'PR', 'PY', 'SR', 'SV', 'TC', 'TT', 'US', 'UY', 'VC', 'VE',
  'VG', 'VI',
  // Afrika
  'AO', 'BJ', 'CI', 'CM', 'CV', 'DZ', 'EG', 'ER', 'ET', 'GA',
  'GH', 'GM', 'GN', 'GQ', 'KE', 'KM', 'LR', 'LY', 'MA', 'MG',
  'ML', 'MR', 'MU', 'MW', 'MZ', 'NA', 'NG', 'SC', 'SD', 'SL',
  'SN', 'SO', 'ST', 'TG', 'TN', 'TZ', 'UG', 'ZA', 'ZM', 'ZW',
  // Okyanusya
  'AU', 'FJ', 'FM', 'IM', 'KI', 'MH', 'NR', 'NZ', 'PF', 'PG',
  'PW', 'SB', 'TO', 'TV', 'VU', 'WS',
] as const;

/**
 * Aktif dilde ülke adını getir (Intl.DisplayNames API)
 */
export function getCountryName(code: string, locale?: string): string {
  const lang = locale || i18next.language || 'tr';
  try {
    const displayNames = new Intl.DisplayNames([lang], { type: 'region' });
    return displayNames.of(code) || code;
  } catch {
    return code;
  }
}

/**
 * Aktif dilde sıralı ülke listesi (Intl.DisplayNames API)
 * Backward compatible: { code, name } formatında döner
 */
export function getCountries(locale?: string): Array<{ code: string; name: string }> {
  const lang = locale || i18next.language || 'tr';
  try {
    const displayNames = new Intl.DisplayNames([lang], { type: 'region' });
    return COUNTRY_CODES.map(code => ({
      code,
      name: displayNames.of(code) || code,
    })).sort((a, b) => a.name.localeCompare(b.name, lang));
  } catch {
    return COUNTRY_CODES.map(code => ({ code, name: code }));
  }
}

/**
 * Backward compatible countries export
 * NOT: Dil değişiminde güncel listeyi almak için getCountries() kullanın
 */
export const countries = getCountries();
